import { Main } from "@/components/Main";
import { useEffect, useState, useRef, useCallback } from "react";
import { getStocksByContest, getStocksDetailed, exportStocks, getContestSummary, ContestSummaryResponse } from "@/Api/stocks";
import { getContestssData } from "@/Api/contests";
import { getSelectUnities } from "@/Api/unities";
import { CreateLoaderBox } from "@/components/Loaders";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Eye,
  X,
  ChevronRight,
  ChevronDown,
  Maximize,
  Minimize,
  RefreshCw,
  Loader,
  Pause,
  Play,
  Search,
  Download,
  FileText,
  FileSpreadsheet,
  ClipboardList,
  Package,
  Building2,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";
import {
  parseProductRanges,
  describeProductRange,
  isIncompleteRange,
} from "@/utils/productSearch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  StockProduct,
  Contest,
  SellerDistribution,
  InProgressSale,
  PendingDistribution,
} from "@/types/stocks";
import { Mask } from "@/utils/mask";
import { getSaleStatusBadge } from "@/constants/saleStatus";
import { SummaryProductsModal } from "./components/SummaryProductsModal";

const AUTO_REFRESH_INTERVAL = 25; // segundos

export const PageList = () => {
  const noShowColumnInProgress = false;

  const [contests, setContests] = useState<Contest[]>([]);
  const [selectedContest, setSelectedContest] = useState<number | null>(null);
  const [stocksData, setStocksData] = useState<StockProduct[]>([]);
  const [state, setState] = useState<"loading" | "production">("loading");
  const [viewMode, setViewMode] = useState<"complete" | "general" | "summary">("complete");
  // Estados de paginação dos concursos
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Estados de paginação dos stocks
  const [stocksCurrentPage, setStocksCurrentPage] = useState(1);
  const [stocksTotalPages, setStocksTotalPages] = useState(1);

  // Estado de busca
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Estados do modal de distribuição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    sellers: SellerDistribution[];
    productName: string;
    unityName: string;
    productClassification: string;
    currentBalance: number;
  } | null>(null);
  const [loadingModal, setLoadingModal] = useState(false);

  // Estados do modal de vendas em andamento
  const [isInProgressModalOpen, setIsInProgressModalOpen] = useState(false);
  const [inProgressModalData, setInProgressModalData] = useState<{
    product_name: string;
    product_classification_name: string;
    in_progress_sales_quantity: number;
    in_progress_sales: InProgressSale[];
  } | null>(null);

  // Estados do modal de distribuições pendentes (não aceitas)
  const [isPendingDistModalOpen, setIsPendingDistModalOpen] = useState(false);
  const [pendingDistModalData, setPendingDistModalData] = useState<{
    product_name: string;
    product_classification_name: string;
    pending_distributions_quantity: number;
    pending_distributions: PendingDistribution[];
  } | null>(null);

  // Estados de fullscreen e refresh
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const [isPaused, setIsPaused] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [contestSummaryData, setContestSummaryData] = useState<ContestSummaryResponse | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Estados para resumo por unidade
  const [unitiesForSummary, setUnitiesForSummary] = useState<{ id: number; name: string }[]>([]);
  const [selectedUnityForSummary, setSelectedUnityForSummary] = useState<number | null>(null);
  const [summaryUnityName, setSummaryUnityName] = useState<string | null>(null);

  // Olhinho do resumo: bolões numerados que compõem uma linha do resumo
  const [summaryProductsTarget, setSummaryProductsTarget] = useState<{
    productClassificationId: number;
    description: string;
  } | null>(null);

  // Carregar concursos ativos com paginação
  useEffect(() => {
    const fetchContests = async (page: number = 1) => {
      try {
        if (page === 1) {
          setState("loading");
        }

        const { data } = await getContestssData(page, {
          contest_status_id: 1,
          is_active: 1,
        });
        const activeContests = data?.response?.data || [];
        const pagination = data?.response;

        if (page === 1) {
          setContests(activeContests);
          setTotalPages(pagination?.last_page || 1);
          setCurrentPage(1);

          // Seleciona o primeiro concurso automaticamente
          if (activeContests.length > 0) {
            setSelectedContest(activeContests[0].id);
          }
        } else {
          setContests((prev) => [...prev, ...activeContests]);
        }

        setState("production");
      } catch (error) {
        console.error("Error fetching contests:", error);
        if (page === 1) {
          setContests([]);
        }
        setState("production");
      }
    };

    fetchContests(1);
  }, []);

  // Carregar unidades para o dropdown de resumo
  useEffect(() => {
    const fetchUnities = async () => {
      try {
        const { data } = await getSelectUnities();
        setUnitiesForSummary(data?.response || []);
      } catch (error) {
        console.error("Error fetching unities:", error);
      }
    };
    fetchUnities();
  }, []);

  // Carregar página específica de concursos
  const loadContestsPage = async (page: number) => {
    if (page <= currentPage || page > totalPages) return;

    try {
      const { data } = await getContestssData(page, {
        contest_status_id: 1,
        is_active: 1,
      });
      const activeContests = data?.response?.data || [];

      setContests((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const uniqueNewContests = activeContests.filter(
          (c: Contest) => !existingIds.has(c.id)
        );
        return [...prev, ...uniqueNewContests];
      });
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching contests page:", error);
    }
  };

  // Carregar mais bolões (stocks)
  const loadMoreStocks = async () => {
    if (!selectedContest || stocksCurrentPage >= stocksTotalPages) return;

    try {
      const nextPage = stocksCurrentPage + 1;
      const { data } = await getStocksByContest(
        selectedContest,
        nextPage,
        searchTerm || undefined
      );
      const stocksResponse = data?.response?.data || [];

      setStocksData((prev) => {
        const existingIds = new Set(prev.map((item) => item.cp_classification_id));
        const uniqueNewItems = stocksResponse.filter(
          (item: StockProduct) => !existingIds.has(item.cp_classification_id)
        );
        return [...prev, ...uniqueNewItems];
      });
      setStocksCurrentPage(nextPage);
    } catch (error) {
      console.error("Error loading more stocks:", error);
    }
  };

  // Função para buscar stocks
  const fetchStocksData = useCallback(
    async (page: number = 1, search?: string) => {
      if (!selectedContest) return;

      try {
        if (page === 1) {
          setState("loading");
        }

        const { data } = await getStocksByContest(
          selectedContest,
          page,
          search || undefined
        );
        const stocksResponse = data?.response?.data || [];

        if (page === 1) {
          setStocksData(stocksResponse);
          setStocksTotalPages(data?.response?.last_page || 1);
          setStocksCurrentPage(1);
        } else {
          setStocksData((prev) => [...prev, ...stocksResponse]);
          setStocksCurrentPage(page);
        }

        setState("production");
      } catch (error) {
        console.error("Error fetching stocks:", error);
        if (page === 1) {
          setStocksData([]);
        }
        setState("production");
      }
    },
    [selectedContest]
  );

  // Carregar dados de estoque quando o concurso for selecionado
  useEffect(() => {
    if (!selectedContest) return;
    fetchStocksData(1, searchTerm);
  }, [selectedContest]);

  // Buscar quando o termo de busca mudar (com debounce)
  const handleSearch = (value: string) => {
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Aguarda o usuário terminar de digitar o intervalo (ex.: "B7 -")
    if (isIncompleteRange(value)) return;

    searchTimeoutRef.current = setTimeout(() => {
      if (selectedContest) {
        fetchStocksData(1, value);
      }
    }, 500);
  };

  // Intervalos identificados no termo de busca (ex.: "B7 - B10")
  const searchRanges = parseProductRanges(searchTerm);

  const uniqueUnities =
    stocksData.length > 0
      ? Array.from(
          new Set(
            stocksData.flatMap((product) =>
              product.unities.map((u) => u.unity_name)
            )
          )
        )
      : [];

  const handleViewDetails = async (
    productId: number,
    productName: string,
    unityId?: number,
    unityName?: string
  ) => {
    if (!selectedContest) return;

    try {
      setLoadingModal(true);
      setIsModalOpen(true);

      const { data } = await getStocksDetailed(
        selectedContest,
        unityId,
        productId
      );

      const detailData = data?.response?.[0];

      if (detailData) {
        setModalData({
          sellers: detailData.distributed_to_sellers || [],
          productName: detailData.product_name || productName,
          unityName: detailData.unity_name || unityName || "Geral",
          productClassification: detailData.product_classification_name || "",
          currentBalance: detailData.current_balance || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching details:", error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleViewInProgressSales = (product: StockProduct) => {
    setInProgressModalData({
      product_name: product.product_name,
      product_classification_name: product.product_classification_name,
      in_progress_sales_quantity: product.in_progress_sales_quantity || 0,
      in_progress_sales: product.in_progress_sales || [],
    });
    setIsInProgressModalOpen(true);
  };

  const handleViewPendingDistributions = (product: StockProduct) => {
    setPendingDistModalData({
      product_name: product.product_name,
      product_classification_name: product.product_classification_name,
      pending_distributions_quantity: product.pending_distributions_quantity || 0,
      pending_distributions: product.pending_distributions || [],
    });
    setIsPendingDistModalOpen(true);
  };

  // Função de refresh manual - mantém a paginação atual
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setCountdown(AUTO_REFRESH_INTERVAL);

    try {
      // Recarrega todas as páginas de concursos já carregadas
      const allContests: Contest[] = [];
      let lastPageTotal = 1;

      for (let page = 1; page <= currentPage; page++) {
        const { data } = await getContestssData(page, {
          contest_status_id: 1,
          is_active: 1,
        });
        const pageContests = data?.response?.data || [];
        allContests.push(...pageContests);
        lastPageTotal = data?.response?.last_page || 1;
      }

      setContests(allContests);
      setTotalPages(lastPageTotal);

      // Se tem um concurso selecionado, recarrega todas as páginas de stocks já carregadas
      if (selectedContest) {
        const allStocks: StockProduct[] = [];
        let stocksLastPage = 1;

        for (let page = 1; page <= stocksCurrentPage; page++) {
          const { data } = await getStocksByContest(
            selectedContest,
            page,
            searchTerm || undefined
          );
          const pageStocks = data?.response?.data || [];
          allStocks.push(...pageStocks);
          stocksLastPage = data?.response?.last_page || 1;
        }

        setStocksData(allStocks);
        setStocksTotalPages(stocksLastPage);
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    selectedContest,
    isRefreshing,
    currentPage,
    stocksCurrentPage,
    searchTerm,
  ]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Export handler
  const handleExport = async (format: "pdf" | "xlsx") => {
    if (!selectedContest) {
      toast.error("Selecione um concurso para exportar");
      return;
    }

    setIsExporting(true);
    try {
      const selectedContestData = contests.find((c) => c.id === selectedContest);
      const blob = await exportStocks({
        contest_id: selectedContest,
        product_name: searchTerm || undefined,
        format,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const contestName = selectedContestData ? `${selectedContestData.lottery.name}-${selectedContestData.name}` : selectedContest;
      a.download = `estoque-${contestName}-${new Date().toISOString().split("T")[0]}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado com sucesso em ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Erro ao exportar estoque");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchContestSummary = useCallback(async () => {
    if (!selectedContest) return;

    setLoadingSummary(true);
    try {
      const { data } = await getContestSummary(
        selectedContest,
        selectedUnityForSummary || undefined
      );
      setContestSummaryData(data?.response || null);
    } catch (error) {
      console.error("Error fetching contest summary:", error);
      setContestSummaryData(null);
    } finally {
      setLoadingSummary(false);
    }
  }, [selectedContest, selectedUnityForSummary]);

  // Buscar dados do resumo quando viewMode for "summary"
  useEffect(() => {
    if (viewMode === "summary" && selectedContest) {
      fetchContestSummary();
    }
  }, [viewMode, selectedContest, selectedUnityForSummary, fetchContestSummary]);

  // Listener para mudanças de fullscreen (ESC, etc)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Auto-refresh a cada 15 segundos com countdown
  useEffect(() => {
    if (isPaused) return;

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleRefresh();
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [handleRefresh, isPaused]);

  // Renderiza a tabela simplificada (só visão geral)
  const renderSimplifiedTable = () => {
    const selectedContestData = contests.find((c) => c.id === selectedContest);

    return (
      <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden max-w-2xl mx-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-primary/10 dark:bg-primary/20">
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary dark:text-primary uppercase">
                  Produto
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-primary dark:text-primary uppercase">
                  Saldo Geral
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                style={{
                  backgroundColor:
                    selectedContestData?.lottery?.color || "#6B7280",
                }}
              >
                <td
                  colSpan={2}
                  className="px-6 py-2.5 text-center text-white font-bold uppercase text-sm"
                >
                  {selectedContestData?.lottery?.name}{" "}
                  {selectedContestData?.name}
                </td>
              </tr>

              {stocksData.map((product, index) => (
                <tr
                  key={product.cp_classification_id}
                  className={cn(
                    "hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors",
                    index % 2 === 0 ? "bg-white dark:bg-zinc-800" : "bg-gray-50/50 dark:bg-zinc-800/50"
                  )}
                >
                  <td className="px-6 py-3.5 text-base font-medium text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700">
                    {product.product_name}
                  </td>
                  <td className="px-6 py-3.5 text-center border-b border-gray-100 dark:border-gray-700">
                    <Badge
                      className={cn(
                        "text-xl font-bold min-w-[60px] px-4 py-2",
                        product.total_overall_balance === 0
                          ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                          : product.total_overall_balance > 0
                          ? "bg-gray-500 hover:bg-gray-600 text-white"
                          : "bg-green-500 hover:bg-green-600 text-white"
                      )}
                    >
                      {product.total_overall_balance}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Renderiza a tabela completa (com todas as unidades)
  const renderCompleteTable = () => {
    const selectedContestData = contests.find((c) => c.id === selectedContest);

    return (
      <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-primary/10 dark:bg-primary/20">
                <th className="px-4 py-3 text-left text-xs font-semibold text-primary uppercase border-b border-gray-200 dark:border-gray-600">
                  Produto
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-primary uppercase border-b border-gray-200 dark:border-gray-600">
                  Geral
                </th>
                {uniqueUnities.map((unityName) => (
                  <th
                    key={unityName}
                    className="px-4 py-3 text-center text-xs font-semibold text-primary uppercase border-b border-gray-200 dark:border-gray-600"
                  >
                    {unityName}
                  </th>
                ))}
                {!noShowColumnInProgress && (
                  <>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase border-b border-amber-400 bg-amber-500">
                      Em Andamento
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase border-b border-red-400 bg-red-500 rounded-tr-lg">
                      Não Aceitos
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              <tr
                style={{
                  backgroundColor:
                    selectedContestData?.lottery?.color || "#6B7280",
                }}
              >
                <td
                  colSpan={
                    uniqueUnities.length + 2 + (!noShowColumnInProgress ? 2 : 0)
                  }
                  className="px-4 py-2.5 text-center text-white font-bold uppercase text-sm"
                >
                  {selectedContestData?.lottery?.name}{" "}
                  {selectedContestData?.name}
                </td>
              </tr>

              {stocksData.map((product, index) => (
                <tr
                  key={product.cp_classification_id}
                  className={cn(
                    "hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors",
                    index % 2 === 0 ? "bg-white dark:bg-zinc-800" : "bg-gray-50/50 dark:bg-zinc-800/50"
                  )}
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      {product.product_name}
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {product.product_classification_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700">
                    <Badge
                      className={cn(
                        "text-lg font-bold min-w-[45px] px-3 py-1",
                        product.total_overall_balance === 0
                          ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                          : product.total_overall_balance > 0
                          ? "bg-gray-500 hover:bg-gray-600 text-white"
                          : "bg-green-500 hover:bg-green-600 text-white"
                      )}
                    >
                      {product.total_overall_balance}
                    </Badge>
                  </td>
                  {uniqueUnities.map((unityName) => {
                    const unity = product.unities.find(
                      (u) => u.unity_name === unityName
                    );
                    const balance = unity?.total_current_balance || 0;

                    return (
                      <td
                        key={unityName}
                        className="px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Badge
                            className={cn(
                              "text-lg font-bold min-w-[45px] px-3 py-1",
                              balance == 0
                                ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                                : balance > 0
                                ? "bg-gray-500 hover:bg-gray-600 text-white"
                                : "bg-green-500 hover:bg-green-600 text-white"
                            )}
                          >
                            {balance}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-full hover:bg-gray-200 dark:hover:bg-zinc-600"
                            onClick={() =>
                              handleViewDetails(
                                product.cp_classification_id,
                                product.product_name,
                                unity?.unity_id,
                                unityName
                              )
                            }
                          >
                            <Eye className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                          </Button>
                        </div>
                      </td>
                    );
                  })}
                  {!noShowColumnInProgress && (
                    <>
                      <td className="px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700 bg-amber-50/50 dark:bg-amber-900/10">
                        <div className="flex items-center justify-center gap-2">
                          <Badge
                            className={cn(
                              "text-lg font-bold min-w-[45px] px-3 py-1 text-white",
                              (product.in_progress_sales_quantity || 0) === 0
                                ? "bg-gray-300 dark:bg-gray-600"
                                : "bg-amber-500 hover:bg-amber-600"
                            )}
                            style={
                              (product.in_progress_sales_quantity || 0) > 0
                                ? {
                                    backgroundColor:
                                      selectedContestData?.lottery?.color ||
                                      "#f59e0b",
                                  }
                                : undefined
                            }
                          >
                            {product.in_progress_sales_quantity || 0}
                          </Badge>
                          {(product.in_progress_sales_quantity || 0) > 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-full hover:bg-amber-100 dark:hover:bg-amber-800/30"
                              onClick={() => handleViewInProgressSales(product)}
                            >
                              <Eye className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/10">
                        <div className="flex items-center justify-center gap-2">
                          <Badge
                            className={cn(
                              "text-lg font-bold min-w-[45px] px-3 py-1 text-white",
                              (product.pending_distributions_quantity || 0) === 0
                                ? "bg-gray-300 dark:bg-gray-600"
                                : "bg-red-500 hover:bg-red-600"
                            )}
                          >
                            {product.pending_distributions_quantity || 0}
                          </Badge>
                          {(product.pending_distributions_quantity || 0) > 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-full hover:bg-red-100 dark:hover:bg-red-800/30"
                              onClick={() => handleViewPendingDistributions(product)}
                            >
                              <Eye className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Renderiza a tabela de resumo completo (com valores)
  const renderSummaryTable = () => {
    const selectedContestData = contests.find((c) => c.id === selectedContest);

    if (loadingSummary) {
      return <CreateLoaderBox cols={4} lines={6} />;
    }

    if (!contestSummaryData) {
      return (
        <div className="text-center py-8 text-gray-500">
          Não foi possível carregar o resumo do concurso
        </div>
      );
    }

    // A visão de resumo é agregada por classificação de produto (sem o nome
    // do bolão), portanto não é afetada pela busca por bolão/intervalo.
    const summaryProducts = contestSummaryData.products;

    return (
      <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th
                  colSpan={3}
                  className="px-4 py-3 text-center text-xs font-semibold text-primary uppercase bg-primary/10 dark:bg-primary/20 border-b border-gray-200 dark:border-gray-600"
                >
                  {summaryUnityName ? (
                    <span className="flex items-center justify-center gap-2">
                      <Building2 className="h-4 w-4" />
                      ESTOQUE - {summaryUnityName}
                    </span>
                  ) : (
                    "ESTOQUE TOTAL"
                  )}
                </th>
                <th
                  colSpan={2}
                  className="px-4 py-3 text-center text-xs font-semibold text-white uppercase bg-amber-500 border-b border-amber-400"
                >
                  EM ANDAMENTO
                </th>
                <th
                  colSpan={2}
                  className="px-4 py-3 text-center text-xs font-semibold text-white uppercase bg-red-500 border-b border-red-400 rounded-tr-lg"
                >
                  NÃO ACEITOS
                </th>
              </tr>
              <tr className="bg-gray-50 dark:bg-zinc-700/50">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase border-b border-gray-200 dark:border-gray-600">
                  Descrição
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase border-b border-gray-200 dark:border-gray-600">
                  Qtd
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase border-b border-r border-gray-200 dark:border-gray-600">
                  Valor
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase border-b border-gray-200 dark:border-gray-600 bg-amber-50/50 dark:bg-amber-900/10">
                  Qtd
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase border-b border-r border-gray-200 dark:border-gray-600 bg-amber-50/50 dark:bg-amber-900/10">
                  Valor
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-red-700 dark:text-red-300 uppercase border-b border-gray-200 dark:border-gray-600 bg-red-50/50 dark:bg-red-900/10">
                  Qtd
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-red-700 dark:text-red-300 uppercase border-b border-gray-200 dark:border-gray-600 bg-red-50/50 dark:bg-red-900/10">
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                style={{
                  backgroundColor: selectedContestData?.lottery?.color || "#6B7280",
                }}
              >
                <td
                  colSpan={7}
                  className="px-4 py-2.5 text-center text-white font-bold uppercase text-sm"
                >
                  {contestSummaryData.contest.lottery} {contestSummaryData.contest.name}
                </td>
              </tr>

              {summaryProducts.map((product, index) => {
                const inProgressQty = product.in_progress_sales?.length || 0;
                const inProgressValue = product.in_progress_sales?.reduce(
                  (sum, sale) => sum + (sale.product_price_total || 0),
                  0
                ) || 0;
                const pendingQty = product.pending_distributions?.reduce(
                  (sum, dist) => sum + (dist.quota_quantity || 0),
                  0
                ) || 0;
                const pendingValue = product.pending_distributions?.reduce(
                  (sum, dist) => sum + (dist.product_price_total || 0),
                  0
                ) || 0;

                return (
                  <tr
                    key={product.product_classification_id ?? product.name}
                    className={cn(
                      "hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors",
                      product.balance === 0
                        ? "bg-yellow-50/50 dark:bg-yellow-900/10"
                        : index % 2 === 0
                          ? "bg-white dark:bg-zinc-800"
                          : "bg-gray-50/50 dark:bg-zinc-800/50"
                    )}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700">
                      <span className="font-semibold">{product.product_name}</span>
                      {product.name && (
                        <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">({product.name})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-center gap-2">
                        <Badge
                          className={cn(
                            "text-lg font-bold min-w-[45px] px-3 py-1",
                            product.balance === 0
                              ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                              : "bg-gray-500 hover:bg-gray-600 text-white"
                          )}
                        >
                          {product.balance}
                        </Badge>
                        {product.balance > 0 && !!product.product_classification_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Ver bolões desta linha"
                            className="h-7 w-7 rounded-full hover:bg-gray-200 dark:hover:bg-zinc-600"
                            onClick={() =>
                              setSummaryProductsTarget({
                                productClassificationId: product.product_classification_id,
                                description: product.name,
                              })
                            }
                          >
                            <Eye className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-r border-gray-100 dark:border-gray-700">
                      {Mask.formatCurrency(product.total_value || 0)}
                    </td>
                    <td className="px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700 bg-amber-50/30 dark:bg-amber-900/5">
                      <Badge
                        className={cn(
                          "text-lg font-bold min-w-[45px] px-3 py-1",
                          inProgressQty === 0
                            ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                            : "bg-amber-500 hover:bg-amber-600 text-white"
                        )}
                      >
                        {inProgressQty}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-amber-700 dark:text-amber-300 border-b border-r border-gray-100 dark:border-gray-700 bg-amber-50/30 dark:bg-amber-900/5">
                      {inProgressQty > 0 ? Mask.formatCurrency(inProgressValue) : "-"}
                    </td>
                    <td className="px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700 bg-red-50/30 dark:bg-red-900/5">
                      <Badge
                        className={cn(
                          "text-lg font-bold min-w-[45px] px-3 py-1",
                          pendingQty === 0
                            ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                            : "bg-red-500 hover:bg-red-600 text-white"
                        )}
                      >
                        {pendingQty}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-red-700 dark:text-red-300 border-b border-gray-100 dark:border-gray-700 bg-red-50/30 dark:bg-red-900/5">
                      {pendingQty > 0 ? Mask.formatCurrency(pendingValue) : "-"}
                    </td>
                  </tr>
                );
              })}

              {/* Linha de totais */}
              {(() => {
                const totalQty = summaryProducts.reduce((sum, p) => sum + (p.balance || 0), 0);
                const totalValue = summaryProducts.reduce((sum, p) => sum + (p.total_value || 0), 0);
                const totalInProgressQty = summaryProducts.reduce(
                  (sum, p) => sum + (p.in_progress_sales?.length || 0),
                  0
                );
                const totalInProgressValue = summaryProducts.reduce(
                  (sum, p) => sum + (p.in_progress_sales?.reduce((s, sale) => s + (sale.product_price_total || 0), 0) || 0),
                  0
                );
                const totalPendingQty = summaryProducts.reduce(
                  (sum, p) => sum + (p.pending_distributions?.reduce((s, dist) => s + (dist.quota_quantity || 0), 0) || 0),
                  0
                );
                const totalPendingValue = summaryProducts.reduce(
                  (sum, p) => sum + (p.pending_distributions?.reduce((s, dist) => s + (dist.product_price_total || 0), 0) || 0),
                  0
                );

                return (
                  <tr className="bg-gray-100 dark:bg-zinc-700 font-bold">
                    <td className="px-4 py-3.5 text-sm font-bold text-gray-900 dark:text-gray-100">
                      TOTAL GERAL
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <Badge className="text-lg font-bold min-w-[45px] px-3 py-1 bg-gray-700 hover:bg-gray-800 text-white">
                        {totalQty}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-center text-sm font-bold text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600">
                      {Mask.formatCurrency(totalValue)}
                    </td>
                    <td className="px-4 py-3.5 text-center bg-amber-100/50 dark:bg-amber-900/20">
                      <Badge className="text-lg font-bold min-w-[45px] px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white">
                        {totalInProgressQty}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-center text-sm font-bold text-amber-700 dark:text-amber-200 bg-amber-100/50 dark:bg-amber-900/20 border-r border-gray-200 dark:border-gray-600">
                      {Mask.formatCurrency(totalInProgressValue)}
                    </td>
                    <td className="px-4 py-3.5 text-center bg-red-100/50 dark:bg-red-900/20">
                      <Badge className="text-lg font-bold min-w-[45px] px-3 py-1 bg-red-600 hover:bg-red-700 text-white">
                        {totalPendingQty}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-center text-sm font-bold text-red-700 dark:text-red-200 bg-red-100/50 dark:bg-red-900/20">
                      {Mask.formatCurrency(totalPendingValue)}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Main>
      <div
        ref={containerRef}
        className={cn(
          "h-full overflow-y-auto px-2 sm:px-4 py-4 sm:py-6 space-y-4",
          isFullscreen && "bg-white dark:bg-zinc-900"
        )}
      >
        <PageHeader
          icon={Package}
          title="Estoques"
          description="Controle de estoque por concurso"
        />

        {/* Filtros e Controles */}
        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Busca */}
            <div className="w-full lg:w-auto">
              <div className="relative w-full lg:w-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar bolão (ex: B7 ou B7 - B10)"
                  title="Busque por um bolão (B7) ou por um intervalo (B7 - B10). É possível combinar: B7 - B10, B15"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9 pr-9 w-full lg:w-[300px]"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => handleSearch("")}
                    aria-label="Limpar busca"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {searchRanges.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {searchRanges.map((range, index) => (
                    <Badge
                      key={`${range.prefix}-${range.start}-${range.end}-${index}`}
                      variant="secondary"
                      className="text-[11px] font-medium"
                    >
                      Intervalo: {describeProductRange(range)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Controles Agrupados */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto">
            {/* Modo de Visualização */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex">
              <button
                onClick={() => setViewMode("general")}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  viewMode === "general"
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                )}
              >
                Geral
              </button>
              <button
                onClick={() => setViewMode("complete")}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  viewMode === "complete"
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                )}
              >
                Completa
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1",
                      viewMode === "summary"
                        ? "bg-amber-500 text-white shadow-sm"
                        : "text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300"
                    )}
                  >
                    <ClipboardList className="h-3.5 w-3.5" />
                    {viewMode === "summary" && summaryUnityName
                      ? `Resumo: ${summaryUnityName}`
                      : "Resumo"}
                    <ChevronDown className="h-3 w-3 ml-0.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedUnityForSummary(null);
                      setSummaryUnityName(null);
                      setViewMode("summary");
                    }}
                    className="flex items-center gap-2"
                  >
                    <ClipboardList className="h-4 w-4 text-amber-500" />
                    <span className="font-medium">Resumo Geral</span>
                  </DropdownMenuItem>
                  {unitiesForSummary.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-gray-500">
                        Resumo por Unidade
                      </DropdownMenuLabel>
                      {unitiesForSummary.map((unity) => (
                        <DropdownMenuItem
                          key={unity.id}
                          onClick={() => {
                            setSelectedUnityForSummary(unity.id);
                            setSummaryUnityName(unity.name);
                            setViewMode("summary");
                          }}
                          className="flex items-center gap-2"
                        >
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span>{unity.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Auto-refresh */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex items-center gap-1">
              <button
                onClick={() => setIsPaused(!isPaused)}
                className={cn(
                  "p-2 rounded-md transition-colors",
                  isPaused
                    ? "bg-yellow-500 text-white"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                )}
                title={isPaused ? "Retomar auto-refresh" : "Pausar auto-refresh"}
              >
                {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </button>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors text-sm",
                  isRefreshing
                    ? "text-gray-400"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                )}
                title="Atualizar agora"
              >
                {isRefreshing ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {!isPaused && !isRefreshing && (
                  <span className="tabular-nums text-xs font-medium">{countdown}s</span>
                )}
              </button>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">Exportar</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport("pdf")}>
                    <FileText className="h-4 w-4 mr-2" />
                    Exportar PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport("xlsx")}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Exportar Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
                className="gap-2"
                title={isFullscreen ? "Sair da tela cheia" : "Tela cheia"}
              >
                {isFullscreen ? (
                  <Minimize className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">{isFullscreen ? "Sair" : "Expandir"}</span>
              </Button>
            </div>
          </div>
        </div>
        </div>

        <Tabs
          value={selectedContest?.toString()}
          onValueChange={(value) => setSelectedContest(Number(value))}
          className="mt-4"
        >
          <div className="space-y-3">
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-1">
                Concursos Ativos
              </p>
              <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
                {contests.map((contest) => (
                  <TabsTrigger
                    key={contest.id}
                    value={contest.id.toString()}
                    className={cn(
                      "px-4 py-2 text-sm font-semibold rounded-lg transition-all",
                      "data-[state=active]:shadow-md",
                      "border-2",
                      selectedContest !== contest.id && "bg-white dark:bg-gray-700 hover:opacity-80"
                    )}
                    style={{
                      backgroundColor:
                        selectedContest === contest.id
                          ? contest.lottery.color
                          : undefined,
                      color: selectedContest === contest.id
                        ? "white"
                        : contest.lottery.color,
                      borderColor: contest.lottery.color,
                    }}
                  >
                    {contest.lottery.name} {contest.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {totalPages > 1 && currentPage < totalPages && (
              <div className="flex items-center justify-center pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => loadContestsPage(currentPage + 1)}
                  className="gap-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Carregar mais concursos
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                    {currentPage}/{totalPages}
                  </span>
                </Button>
              </div>
            )}
          </div>

          {state === "loading" ? (
            <CreateLoaderBox cols={4} lines={6} />
          ) : (
            contests.map((contest) => (
              <TabsContent key={contest.id} value={contest.id.toString()}>
                <div className="space-y-4">
                  {viewMode === "general"
                    ? renderSimplifiedTable()
                    : viewMode === "summary"
                    ? renderSummaryTable()
                    : renderCompleteTable()}

                  {viewMode !== "summary" && stocksTotalPages > 1 &&
                    stocksCurrentPage < stocksTotalPages && (
                      <div className="flex items-center justify-center py-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={loadMoreStocks}
                          className="gap-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Carregar mais bolões
                          <ChevronRight className="h-4 w-4" />
                          <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                            {stocksCurrentPage}/{stocksTotalPages}
                          </span>
                        </Button>
                      </div>
                    )}
                </div>
              </TabsContent>
            ))
          )}
        </Tabs>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">
                  {modalData?.productName}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                  {modalData?.unityName} - {modalData?.productClassification}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsModalOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>

          {loadingModal ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-100 dark:bg-zinc-800 p-4 rounded-lg">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Saldo atual na unidade
                </div>
                <div className="text-2xl font-bold">
                  {modalData?.currentBalance || 0} cotas
                </div>
              </div>

              {modalData?.sellers && modalData.sellers.length > 0 ? (
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Distribuição por Vendedor
                  </h3>
                  <div className="bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-zinc-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Vendedor
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Distribuídas
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Disponíveis
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {modalData.sellers.map((seller) => (
                          <tr
                            key={seller.seller_id}
                            className="hover:bg-gray-50 dark:hover:bg-zinc-700/50"
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {seller.seller_name}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant="outline" className="font-mono">
                                {seller.distributed_quantity}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge
                                className={cn(
                                  "font-mono font-bold",
                                  seller.seller_balance === 0
                                    ? "bg-yellow-500 text-white"
                                    : "bg-green-500 text-white"
                                )}
                              >
                                {seller.seller_balance}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                      <div className="text-xs text-blue-600 dark:text-blue-400">
                        Total Distribuído
                      </div>
                      <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                        {modalData.sellers.reduce(
                          (sum, s) => sum + s.distributed_quantity,
                          0
                        )}
                      </div>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                      <div className="text-xs text-yellow-600 dark:text-yellow-400">
                        Total Disponível
                      </div>
                      <div className="text-xl font-bold text-yellow-700 dark:text-yellow-300">
                        {modalData.sellers.reduce(
                          (sum, s) => sum + s.seller_balance,
                          0
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nenhum vendedor com distribuição nesta unidade
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isInProgressModalOpen}
        onOpenChange={setIsInProgressModalOpen}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">
                  {inProgressModalData?.product_name}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                  {inProgressModalData?.product_classification_name}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsInProgressModalOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
              <div className="text-sm text-amber-600 dark:text-amber-400">
                Cotas com vendas em andamento
              </div>
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {inProgressModalData?.in_progress_sales_quantity || 0}{" "}
                {(inProgressModalData?.in_progress_sales_quantity || 0) === 1
                  ? "cota"
                  : "cotas"}
              </div>
            </div>

            {inProgressModalData?.in_progress_sales &&
            inProgressModalData.in_progress_sales.length > 0 ? (
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Vendas em Andamento
                </h3>
                <div className="bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">ID</TableHead>
                          <TableHead className="text-xs">Cliente</TableHead>
                          <TableHead className="text-xs">Vendedor</TableHead>
                          <TableHead className="text-xs">Unidade</TableHead>
                          <TableHead className="text-xs">Loteria</TableHead>
                          <TableHead className="text-xs">
                            Tipo de Venda
                          </TableHead>
                          <TableHead className="text-xs text-center">
                            Qtd
                          </TableHead>
                          <TableHead className="text-xs">Valor Total</TableHead>
                          <TableHead className="text-xs text-center">
                            Status
                          </TableHead>
                          <TableHead className="text-xs text-center">
                            Data/Hora
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inProgressModalData.in_progress_sales.map((sale) => (
                          <TableRow key={sale.sale_id}>
                            <TableCell className="text-sm font-medium">
                              #{sale.sale_id}
                            </TableCell>
                            <TableCell className="text-sm">
                              {sale.client?.name || "-"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {sale.seller?.name || "-"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {sale.unity?.name || "-"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {sale.lottery?.name || "-"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {sale.sale_type?.name || "-"}
                            </TableCell>
                            <TableCell className="text-sm text-center">
                              {sale.quantity}
                            </TableCell>
                            <TableCell className="text-sm">
                              {Mask.formatCurrency(sale.sale_total)}
                            </TableCell>
                            <TableCell className="text-center">
                              <span
                                className={cn(
                                  "px-2.5 py-0.5 rounded text-xs font-medium",
                                  getSaleStatusBadge(sale.sale_status?.id || 1)
                                )}
                              >
                                {sale.sale_status?.name || "Em andamento"}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="text-sm">
                                <div>
                                  {sale.sale_created_at
                                    ? new Date(
                                        new Date(
                                          sale.sale_created_at
                                        ).getTime() -
                                          3 * 60 * 60 * 1000
                                      ).toLocaleDateString("pt-BR")
                                    : "-"}
                                </div>
                                <div className="text-gray-500 text-xs">
                                  {sale.sale_created_at
                                    ? new Date(
                                        new Date(
                                          sale.sale_created_at
                                        ).getTime() -
                                          3 * 60 * 60 * 1000
                                      ).toLocaleTimeString("pt-BR", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                      })
                                    : "-"}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Nenhuma venda em andamento para este produto
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isPendingDistModalOpen}
        onOpenChange={setIsPendingDistModalOpen}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">
                  {pendingDistModalData?.product_name}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                  {pendingDistModalData?.product_classification_name}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsPendingDistModalOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <div className="text-sm text-red-600 dark:text-red-400">
                Cotas distribuídas aguardando aceitação
              </div>
              <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                {pendingDistModalData?.pending_distributions_quantity || 0}{" "}
                {(pendingDistModalData?.pending_distributions_quantity || 0) === 1
                  ? "cota"
                  : "cotas"}
              </div>
            </div>

            {pendingDistModalData?.pending_distributions &&
            pendingDistModalData.pending_distributions.length > 0 ? (
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Distribuições Pendentes
                </h3>
                <div className="bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Tipo</TableHead>
                          <TableHead className="text-xs">Destino</TableHead>
                          <TableHead className="text-xs text-center">
                            Qtd
                          </TableHead>
                          <TableHead className="text-xs text-center">
                            Status
                          </TableHead>
                          <TableHead className="text-xs">Criado por</TableHead>
                          <TableHead className="text-xs">Unidade Origem</TableHead>
                          <TableHead className="text-xs text-center">
                            Data/Hora
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingDistModalData.pending_distributions.map((dist) => (
                          <TableRow key={dist.distribution_id}>
                            <TableCell className="text-sm whitespace-nowrap">
                              {dist.distribution_type?.name || "-"}
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              {dist.to_seller?.name || dist.to_unity?.name || "-"}
                            </TableCell>
                            <TableCell className="text-sm text-center font-semibold">
                              {dist.quota_quantity}
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <span className="px-2.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                {dist.status?.name || "Pendente"}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              {dist.created_by || "-"}
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              {dist.from_seller?.unity?.name || dist.from_unity?.name || "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="text-sm">
                                <div>
                                  {dist.distribution_created_at
                                    ? new Date(
                                        new Date(
                                          dist.distribution_created_at
                                        ).getTime() -
                                          3 * 60 * 60 * 1000
                                      ).toLocaleDateString("pt-BR")
                                    : "-"}
                                </div>
                                <div className="text-gray-500 text-xs">
                                  {dist.distribution_created_at
                                    ? new Date(
                                        new Date(
                                          dist.distribution_created_at
                                        ).getTime() -
                                          3 * 60 * 60 * 1000
                                      ).toLocaleTimeString("pt-BR", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                      })
                                    : "-"}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Nenhuma distribuição pendente para este produto
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SummaryProductsModal
        open={!!summaryProductsTarget}
        onOpenChange={(open) => {
          if (!open) setSummaryProductsTarget(null);
        }}
        contestId={selectedContest}
        productClassificationId={summaryProductsTarget?.productClassificationId ?? null}
        description={summaryProductsTarget?.description ?? ""}
        unityId={selectedUnityForSummary}
        unityName={summaryUnityName}
        contestName={contestSummaryData?.contest.name}
        lotteryName={contestSummaryData?.contest.lottery}
        lotteryColor={contests.find((c) => c.id === selectedContest)?.lottery?.color}
      />
    </Main>
  );
};

export default PageList;
